/** Schedule templates (Step 9): reusable starting points for ISO / Agent / Subagent Schedule A. */
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import useAuthStore from '../../lib/auth-store';
import { Card, btnPrimary, btnSmall, errorText, inputCls } from '../finance/financeUi';

const UNITS = ['per_transaction', 'bps', 'flat_monthly', 'flat_annual', 'percent_of_revenue'];
const blankLine = () => ({ fee_code: '', label: '', unit: 'per_transaction', amount: '' });

export function ScheduleTemplatesView() {
  const role = useAuthStore((s) => s.user?.role);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: '', target_role: 'agent', provider_slug: '', items: [blankLine()] });
  const load = useCallback(() => { api.request('/team-economics/catalog/schedule-templates').then(setRows).catch((e) => toast.error(errorText(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const line = (i, patch) => setForm({ ...form, items: form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });
  const save = async () => {
    try {
      await api.request('/team-economics/catalog/schedule-templates', { method: 'POST', body: JSON.stringify({
        ...form, provider_slug: form.provider_slug || null,
        items: form.items.map((it) => ({ ...it, fee_code: it.fee_code || it.label.toLowerCase().replace(/\W+/g, '_'), amount: Number(it.amount || 0) })) }) });
      toast.success('Schedule template saved'); setForm({ name: '', target_role: 'agent', provider_slug: '', items: [blankLine()] }); load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const roles = role === 'super_admin' ? [['iso', 'ISO'], ['agent', 'Agent'], ['subagent', 'Subagent']] : [['agent', 'Agent'], ['subagent', 'Subagent']];
  return (
    <div className="space-y-6" data-testid="schedule-templates-view">
      <Card title="Schedule Templates">
        <p className="px-5 pt-3 text-xs text-muted-foreground">A template is only a starting point. Assigning it creates the person's own effective-dated schedule — editing a template never rewrites schedules already assigned.</p>
        <ul className="divide-y text-sm">
          {rows.map((t) => (
            <li key={t.template_id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3" data-testid={`schedule-template-${t.template_id}`}>
              <span className="font-medium">{t.name}</span>
              <span className="text-xs text-muted-foreground">{t.target_role ? `For ${t.target_role}` : 'Any role'} · {t.provider_slug || 'any platform'} · {(t.items || []).length} line(s)</span>
            </li>
          ))}
          {!rows.length && <li className="px-5 py-8 text-center text-muted-foreground">No Schedule templates yet.</li>}
        </ul>
      </Card>
      <Card title="New template">
        <div className="space-y-3 px-5 py-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <input className={inputCls} placeholder="Template name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" />
            <select className={inputCls} value={form.target_role} onChange={(e) => setForm({ ...form, target_role: e.target.value })} data-testid="template-role-select">
              {roles.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input className={inputCls} placeholder="Platform slug (optional)" value={form.provider_slug} onChange={(e) => setForm({ ...form, provider_slug: e.target.value })} data-testid="template-platform-input" />
          </div>
          {form.items.map((it, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 sm:grid-cols-[1.5fr_1fr_1fr_auto]" data-testid={`template-line-${i}`}>
              <input className={inputCls} placeholder="Line label" value={it.label} onChange={(e) => line(i, { label: e.target.value })} data-testid={`template-line-label-${i}`} />
              <select className={inputCls} value={it.unit} onChange={(e) => line(i, { unit: e.target.value })} data-testid={`template-line-unit-${i}`}>
                {UNITS.map((u) => <option key={u} value={u}>{u.replace(/_/g, ' ')}</option>)}
              </select>
              <input type="number" step="0.0001" className={inputCls} placeholder="Amount" value={it.amount} onChange={(e) => line(i, { amount: e.target.value })} data-testid={`template-line-amount-${i}`} />
              <button type="button" className={btnSmall} aria-label="Remove line" onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })} data-testid={`template-line-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <button type="button" className={btnSmall} onClick={() => setForm({ ...form, items: [...form.items, blankLine()] })} data-testid="template-add-line-btn"><Plus className="mr-1 inline h-3.5 w-3.5" />Add line</button>
            <button type="button" className={btnPrimary} disabled={!form.name || !form.items.length || form.items.some((it) => !it.label)} onClick={save} data-testid="template-save-btn">Save template</button>
          </div>
        </div>
      </Card>
    </div>
  );
}
